import React from 'react';
import { setIn, useFormikContext } from 'formik';

import MenuItem from '~/components/core/Atomic/MenuItem';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { RecoveryRequestListResponse } from '~/server_shared/openapi/data-contracts';

import { useClaimRecoveryRequests, usePayerNameById, useRecoveryRequestsClaimConfig } from './useRecoveryRequests';

export const SETTLES_RECOVERY_REQUEST_FIELD_ID = 'settles_recovery_request_id';

/**
 * The contact field both Add Recovery dialogs carry. `ContactTextFieldFormik` keeps its value under
 * `<id>_id` and the name it renders under `<id>_full_name`, which is what this field writes when a
 * request names its payer.
 */
export const RECOVERED_FROM_CONTACT_FIELD_ID = 'recovered_from_contact';

/** '' is the unset value the shared select clears back to; a picked request is its id. */
export type SettlesRecoveryRequestValue = number | '';

type OpenRecoveryRequest = NonNullable<RecoveryRequestListResponse['recovery_requests']>[number];

/** An open request whose payable type a general recovery on this allocation can actually be posted to. */
type PostableRecoveryRequest = OpenRecoveryRequest & { payable_type: string };

/** What a picked request settles for the enclosing dialog, beyond the payer this field writes itself. */
export interface PickedRequestAllocation {
  payableType: string;
  generalRecoveryType: string | null;
}

/**
 * The payable types a general recovery on this allocation can be posted to, and so the only ones a
 * request offered here may name.
 *
 * Claim-level money is general expenses and nothing else: `_assert_postable_allocation` refuses a
 * claim-level indemnity request outright, because no general-recovery write path could post its
 * settlement. At exposure level both are postable, and the picked request decides which.
 *
 * The same check excludes a request naming neither - `recovery_request.payable_type` is a nullable
 * column and rows predate the create-time validator that now requires it. `link_settlement` matches
 * `RecoveryAmount.payable_type == row.payable_type`, which a null never satisfies, so such a request
 * would be refused and take the recovery down with it.
 */
const postablePayableTypes = (exposureIdOrNullIfGeneralExpenses: number | null): string[] =>
  exposureIdOrNullIfGeneralExpenses === null ? ['expenses'] : ['indemnity', 'expenses'];

const isPostableHere = (
  request: OpenRecoveryRequest,
  exposureIdOrNullIfGeneralExpenses: number | null
): request is PostableRecoveryRequest =>
  request.exposure_id === exposureIdOrNullIfGeneralExpenses &&
  postablePayableTypes(exposureIdOrNullIfGeneralExpenses).includes(request.payable_type ?? '');

interface SettlesRecoveryRequestFieldProps {
  /** the recovery this action writes: claim-level general expenses when null */
  exposureIdOrNullIfGeneralExpenses: number | null;
  /**
   * Folds the picked request's own allocation into the dialog's values. The shape is the dialog's -
   * a `payable_type` string and a flat recovery-type field on the single dialog, a `payable_types`
   * array and one seeded allocation row on the split one - so each dialog supplies its own.
   */
  applyPickedRequest: (values: Record<string, unknown>, allocation: PickedRequestAllocation) => Record<string, unknown>;
  disabled?: boolean;
}

/**
 * The whole manual-settlement UI: the first field on the Add Recovery dialog, naming the recovery
 * request this money answers. Leaving it alone records a general recovery exactly as before.
 *
 * It comes first because it determines everything under it. The request already knows the exposure,
 * the payable type and the payer, and only the exposure is fixed by the dialog's own context - so a
 * pick fills in and locks the other two, and every value below is a confirmation rather than a
 * re-entry. That ordering is also what keeps the field from ever being invisible: filtered by a
 * payable type chosen above it, an adjuster settling an expenses request who landed on Indemnity
 * first saw no field at all and no way to find out that another radio button would reveal it.
 *
 * Offered only where the claim's configured provider is `manual` (`can_settle_manually`). Under a
 * real provider the money settles itself - the acknowledgement webhook calls `settle()`, which
 * posts its own recovery amount - so a hand-entered attribution on top of that would leave two
 * recovery amounts counted against one payment.
 *
 * Only the requests this action could actually settle are offered: same exposure, and a payable type
 * the recovery can be posted to at all. The settlement link demands every recovery it attributes sit
 * in the allocation the request named, and the route writes the link in the same uncommitted
 * transaction as the recovery, so a request outside that allocation does not merely fail to link -
 * the whole action rolls back on a 400. Matching the payable type is what the *pick* now achieves
 * rather than what the filter assumes.
 *
 * Not gated on the rollout flag, nor on the module's own `is_enabled`. Those gate *creating* a
 * request; money arriving against one already sent still has to be recordable after they are turned
 * off. A claim with no open request in this exposure renders nothing at all, which is every claim in
 * an organization that has never sent one.
 */
const SettlesRecoveryRequestField: React.FC<SettlesRecoveryRequestFieldProps> = ({
  exposureIdOrNullIfGeneralExpenses,
  applyPickedRequest,
  disabled,
}) => {
  const { currencyFormatter } = useCurrencyFormatter();
  const payerNameById = usePayerNameById();
  const { values, setValues } = useFormikContext<Record<string, unknown>>();
  const { data: claimConfig } = useRecoveryRequestsClaimConfig();
  const canSettleManually = !!claimConfig?.can_settle_manually;
  const { data } = useClaimRecoveryRequests({ openOnly: true, shouldFetch: canSettleManually });

  const settleableRequests = React.useMemo(
    () =>
      (data?.recovery_requests ?? []).filter((request) => isPostableHere(request, exposureIdOrNullIfGeneralExpenses)),
    [data?.recovery_requests, exposureIdOrNullIfGeneralExpenses]
  );

  const options = React.useMemo(
    () =>
      settleableRequests.map((request) => ({
        value: request.id,
        // names the request by who owes it and how much was asked for; the id behind the option
        // keeps the pick unambiguous even where two labels read alike
        label: `${payerNameById.get(request.payer_contact_id) ?? 'Unknown payer'} - ${currencyFormatter.format(
          request.requested_amount,
          request.currency ?? undefined
        )}`,
      })),
    [settleableRequests, payerNameById, currencyFormatter]
  );

  const selected = values[SETTLES_RECOVERY_REQUEST_FIELD_ID] as SettlesRecoveryRequestValue | undefined;
  const appliedRequestId = React.useRef<SettlesRecoveryRequestValue>('');

  // Picking a request fills in the facts the request already carries, so none of them is captured a
  // second time. Neither the payer nor the payable type is a free choice once a request is named:
  // `settle()` attributes provider-confirmed money to `payer_contact_id` and posts it to the
  // request's own allocation, so a different contact or payable type here would record this recovery
  // against something the request never named - and the settlement link refuses the mismatch, taking
  // the recovery with it. The enclosing dialog locks both for as long as a request is picked.
  //
  // Applied once per pick rather than on every render: the recovery type stays editable, and
  // re-applying it would undo the adjuster's own edit.
  React.useEffect(() => {
    if (appliedRequestId.current === (selected ?? '')) {
      return;
    }
    appliedRequestId.current = selected ?? '';

    const request = settleableRequests.find((candidate) => candidate.id === selected);
    if (!request) {
      return;
    }

    // One `setValues` rather than a setFieldValue per field, which race in Formik - each runs
    // validation against the values as they stood before its siblings.
    let nextValues = setIn(values, `${RECOVERED_FROM_CONTACT_FIELD_ID}_id`, request.payer_contact_id);
    nextValues = setIn(
      nextValues,
      `${RECOVERED_FROM_CONTACT_FIELD_ID}_full_name`,
      payerNameById.get(request.payer_contact_id) ?? ''
    );
    setValues(
      applyPickedRequest(nextValues, {
        payableType: request.payable_type,
        generalRecoveryType: request.general_recovery_type,
      })
    );
  }, [selected, settleableRequests, payerNameById, applyPickedRequest, values, setValues]);

  if (!canSettleManually || options.length === 0) {
    return null;
  }

  return (
    <TextFieldFormik
      id={SETTLES_RECOVERY_REQUEST_FIELD_ID}
      label="Settles Recovery Request"
      select
      fullWidth
      disabled={disabled}
      // optional in both directions: a lone open request must not fill itself in, and a pick made
      // by mistake has to be undoable without reopening the dialog
      autoSelectSingleOption={false}
      clearable
    >
      {/* @ts-expect-error - TextFieldFormik not yet typed and its type inferrence is flawed */}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextFieldFormik>
  );
};

export default SettlesRecoveryRequestField;
